/**
 * FrameEffectsWorkerBridge — main-thread manager for the FrameEffectsWorker.
 *
 * Single point of contact for the rest of the codebase. Handles:
 *  - Lazy worker spawn (only on first processFrame / isAvailable check)
 *  - OffscreenCanvas + feature-flag gating
 *  - Message routing via requestId → pending-promise map
 *  - Per-element backpressure (skip if previous frame still in-flight)
 *  - Worker crash recovery (resolve all pending as null, mark unavailable)
 *  - Clean termination
 *
 * When the worker is unavailable (missing OffscreenCanvas, flag off, crash),
 * `processFrame` returns `null` so callers fall back to inline processing.
 */

import type {
  FrameEffectRequest,
  FrameEffectResult,
  FrameEffectError,
} from "./FrameEffectsWorker";
import { isWorkerEffectsEnabled } from "../utils/studioFeatureFlags";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Effects configuration accepted by the bridge (mirrors worker protocol). */
export type EffectsConfig = FrameEffectRequest["effects"];

export interface FrameEffectsBridge {
  /** Returns true if the worker is available (OffscreenCanvas supported + flag ON). */
  isAvailable(): boolean;

  /**
   * Send a frame to the worker for processing.
   * Returns null if unavailable, backpressure, or on error — caller falls
   * back to inline processing.
   */
  processFrame(
    elementId: string,
    frame: ImageBitmap,
    width: number,
    height: number,
    effects: EffectsConfig,
  ): Promise<ImageBitmap | null>;

  /** Terminate the worker and clean up all resources. Safe to call multiple times. */
  terminate(): void;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

let _idCounter = 0;

function nextRequestId(): string {
  return `fxb-${++_idCounter}-${Date.now()}`;
}

export function createFrameEffectsBridge(): FrameEffectsBridge {
  let _worker: Worker | null = null;
  let _dead = false; // true after crash or terminate — never respawns

  /** Pending requests waiting for worker response. */
  const _pending = new Map<
    string,
    { resolve: (result: ImageBitmap | null) => void; elementId: string }
  >();

  /** Elements that currently have an in-flight request (backpressure). */
  const _inFlight = new Set<string>();

  // -----------------------------------------------------------------------
  // Capability check
  // -----------------------------------------------------------------------

  function _canSpawn(): boolean {
    return (
      typeof OffscreenCanvas !== "undefined" && isWorkerEffectsEnabled()
    );
  }

  // -----------------------------------------------------------------------
  // Worker lifecycle
  // -----------------------------------------------------------------------

  function _ensureWorker(): Worker | null {
    if (_dead) return null;
    if (_worker) return _worker;
    if (!_canSpawn()) return null;

    try {
      _worker = new Worker(
        new URL("./FrameEffectsWorker.ts", import.meta.url),
        { type: "module" },
      );

      _worker.onmessage = _onMessage;
      _worker.onerror = _onError;

      return _worker;
    } catch {
      _dead = true;
      return null;
    }
  }

  // -----------------------------------------------------------------------
  // Message handling
  // -----------------------------------------------------------------------

  function _onMessage(
    event: MessageEvent<FrameEffectResult | FrameEffectError>,
  ): void {
    const msg = event.data;
    const entry = _pending.get(msg.requestId);
    if (!entry) return;

    _pending.delete(msg.requestId);
    _inFlight.delete(entry.elementId);

    if (msg.type === "frame-result") {
      entry.resolve(msg.processed);
    } else {
      // frame-error — resolve null so caller falls back to inline
      entry.resolve(null);
    }
  }

  function _onError(): void {
    // Worker crashed — mark dead, resolve all pending as null
    _dead = true;
    _drainPending();
    _killWorker();
  }

  // -----------------------------------------------------------------------
  // Cleanup helpers
  // -----------------------------------------------------------------------

  function _drainPending(): void {
    for (const [, entry] of _pending) {
      entry.resolve(null);
    }
    _pending.clear();
    _inFlight.clear();
  }

  function _killWorker(): void {
    if (_worker) {
      try {
        _worker.terminate();
      } catch {
        /* best effort */
      }
      _worker = null;
    }
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  function isAvailable(): boolean {
    if (_dead) return false;
    return _canSpawn();
  }

  async function processFrame(
    elementId: string,
    frame: ImageBitmap,
    width: number,
    height: number,
    effects: EffectsConfig,
  ): Promise<ImageBitmap | null> {
    const worker = _ensureWorker();
    if (!worker) {
      frame.close();
      return null;
    }

    // Backpressure: if this element already has an in-flight request,
    // skip this frame and let the caller reuse the last result.
    if (_inFlight.has(elementId)) {
      frame.close();
      return null;
    }

    const requestId = nextRequestId();
    _inFlight.add(elementId);

    const promise = new Promise<ImageBitmap | null>((resolve) => {
      _pending.set(requestId, { resolve, elementId });
    });

    const msg: FrameEffectRequest = {
      type: "process-frame",
      requestId,
      elementId,
      frame,
      width,
      height,
      effects,
    };

    try {
      worker.postMessage(msg, [frame] as unknown as Transferable[]);
    } catch {
      // postMessage failed (e.g., frame already detached)
      _pending.delete(requestId);
      _inFlight.delete(elementId);
      return null;
    }

    return promise;
  }

  function terminate(): void {
    _dead = true;
    _drainPending();
    _killWorker();
  }

  return { isAvailable, processFrame, terminate };
}
